import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { gfm } from "micromark-extension-gfm";
import { sha256HexAsync } from "@/lib/sha256";

export const HEADING_CONTRACT_VERSION = 1;
const TOOL_MARKER_RE = /<<TOOL_CALL\|(?:start|end|error)\|[\s\S]*?>>/g;

export interface ConversationHeading {
  key: string;
  level: number;
  ordinal: number;
  text: string;
  contentFingerprint: string;
}

export function normalizeConversationHeadingText(value: string): string {
  return value.normalize("NFC").replace(/\s+/gu, " ").trim();
}

function plainText(node: any): string {
  if (node.type === "text" || node.type === "inlineCode" || node.type === "inlineMath") {
    return node.value ?? "";
  }
  if (node.type === "image") return node.alt ?? "";
  if (node.type === "break") return " ";
  return Array.isArray(node.children)
    ? node.children.map((child: any) => plainText(child)).join("")
    : "";
}

async function sha256Prefix(value: string, length = 16): Promise<string> {
  return (await sha256HexAsync(value)).slice(0, length);
}

/** Extract headings from the exact assistant Markdown grammar rendered by Streamdown. */
export async function extractConversationHeadings(markdown: string): Promise<ConversationHeading[]> {
  const source = markdown.replace(TOOL_MARKER_RE, "");
  const root = fromMarkdown(source, {
    extensions: [gfm()],
    mdastExtensions: [gfmFromMarkdown()],
  });
  const raw: Array<{ level: number; text: string }> = [];
  const visit = (node: any) => {
    if (node.type === "heading") {
      raw.push({ level: node.depth, text: normalizeConversationHeadingText(plainText(node)) });
    }
    if (Array.isArray(node.children)) node.children.forEach(visit);
  };
  visit(root);

  const contentFingerprint = await sha256Prefix(JSON.stringify(raw), 32);
  const occurrences = new Map<string, number>();
  return Promise.all(raw.map(async ({ level, text }, ordinal) => {
    const occurrence = (occurrences.get(text) ?? 0) + 1;
    occurrences.set(text, occurrence);
    return {
      key: `h1-${await sha256Prefix(text)}-${occurrence}`,
      level,
      ordinal,
      text,
      contentFingerprint,
    };
  }));
}

function domToken(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, "-");
}

export function conversationMessageDomId(surfaceId: string, messageId: string): string {
  return `conversation-${domToken(surfaceId)}-message-${domToken(messageId)}`;
}

export function conversationHeadingDomId(surfaceId: string, messageId: string, headingKey: string): string {
  return `conversation-${domToken(surfaceId)}-message-${domToken(messageId)}-${domToken(headingKey)}`;
}
